import * as React from 'react'
import {
  CommandBase as Command,
  CommandDialogBase as CommandDialog,
  CommandInputBase as CommandInput,
  CommandListBase as CommandList,
  CommandEmptyBase as CommandEmpty,
  CommandGroupBase as CommandGroup,
  CommandItemBase as CommandItem,
  CommandShortcutBase as CommandShortcut,
  CommandSeparatorBase as CommandSeparator,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

/**
 * Figma (node 204:1144 — Command):
 *
 * Container (node 204:1143):
 *   bg-popover, border border-border, rounded-lg (10px), overflow-clip, p-1, max-w-[384px]
 *
 * Input (node 21183:13989):
 *   h-9 (36px), rounded-lg, bg-input/30, px-2.5, gap-1.5
 *   Search icon (16px) + placeholder text-sm text-muted-foreground
 *
 * Item (node 345:8657):
 *   h-8 (32px), rounded-sm (6px), px-2, py-1.5, gap-2, text-sm
 *   Variants: Default (text only), Icon (icon + text)
 *   States: Default, Hover (bg-accent), Disabled (opacity-50)
 *   Selected: checkmark on right
 *
 * Group (node 21183:25041):
 *   p-1
 *
 * Label / Heading:
 *   text-xs (12px), font-medium, text-muted-foreground, px-2, py-1.5
 *
 * Separator (node 345:8826):
 *   h-px bg-border, -mx-1
 *
 * Empty (node 21183:14678):
 *   text-sm text-muted-foreground, centered "No results found."
 *
 * Shortcut:
 *   text-xs, text-muted-foreground, ml-auto
 */

/* ─── Root ─── */
const IntuiCommand = React.forwardRef<
  React.ElementRef<typeof Command>,
  React.ComponentPropsWithoutRef<typeof Command>
>(({ className, ...props }, ref) => (
  <Command
    ref={ref}
    className={cn('rounded-lg border bg-popover shadow-md', className)}
    {...props}
  />
))
IntuiCommand.displayName = 'IntuiCommand'

/* ─── Dialog ─── */
/* Thin wrapper to avoid TDZ — circular dep means CommandDialog is uninitialized at module scope */
const IntuiCommandDialog = (props: React.ComponentProps<typeof CommandDialog>) => <CommandDialog {...props} />

/* ─── Input ─── */
const IntuiCommandInput = React.forwardRef<
  React.ElementRef<typeof CommandInput>,
  React.ComponentPropsWithoutRef<typeof CommandInput>
>(({ className, ...props }, ref) => (
  <CommandInput
    ref={ref}
    className={cn('h-9', className)}
    {...props}
  />
))
IntuiCommandInput.displayName = 'IntuiCommandInput'

/* ─── List ─── */
const IntuiCommandList = React.forwardRef<
  React.ElementRef<typeof CommandList>,
  React.ComponentPropsWithoutRef<typeof CommandList>
>(({ className, ...props }, ref) => (
  <CommandList ref={ref} className={className} {...props} />
))
IntuiCommandList.displayName = 'IntuiCommandList'

/* ─── Empty ─── */
const IntuiCommandEmpty = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof CommandEmpty>
>(({ className, ...props }, ref) => (
  <CommandEmpty
    ref={ref}
    className={cn('text-muted-foreground', className)}
    {...props}
  />
))
IntuiCommandEmpty.displayName = 'IntuiCommandEmpty'

/* ─── Group ─── */
const IntuiCommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandGroup>,
  React.ComponentPropsWithoutRef<typeof CommandGroup>
>(({ className, ...props }, ref) => (
  <CommandGroup ref={ref} className={className} {...props} />
))
IntuiCommandGroup.displayName = 'IntuiCommandGroup'

/* ─── Item ─── */
export interface IntuiCommandItemProps
  extends React.ComponentPropsWithoutRef<typeof CommandItem> {
  /** Optional icon rendered before the label */
  icon?: React.ReactNode
  /** Keyboard shortcut displayed on the right */
  shortcut?: string
}

const IntuiCommandItem = React.forwardRef<
  React.ElementRef<typeof CommandItem>,
  IntuiCommandItemProps
>(({ className, icon, shortcut, children, ...props }, ref) => (
  <CommandItem
    ref={ref}
    className={cn('rounded-sm px-2 py-1.5 gap-2 text-sm', className)}
    {...props}
  >
    {icon && (
      <span className="flex items-center justify-center [&>svg]:size-4 [&>svg]:shrink-0">
        {icon}
      </span>
    )}
    {children}
    {shortcut && <CommandShortcut>{shortcut}</CommandShortcut>}
  </CommandItem>
))
IntuiCommandItem.displayName = 'IntuiCommandItem'

/* ─── Separator ─── */
const IntuiCommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandSeparator>,
  React.ComponentPropsWithoutRef<typeof CommandSeparator>
>(({ className, ...props }, ref) => (
  <CommandSeparator ref={ref} className={cn(className)} {...props} />
))
IntuiCommandSeparator.displayName = 'IntuiCommandSeparator'

/* ─── Shortcut (convenience re-export) ─── */
const IntuiCommandShortcut = (props: React.ComponentProps<typeof CommandShortcut>) => <CommandShortcut {...props} />

export {
  IntuiCommand,
  IntuiCommandDialog,
  IntuiCommandInput,
  IntuiCommandList,
  IntuiCommandEmpty,
  IntuiCommandGroup,
  IntuiCommandItem,
  IntuiCommandSeparator,
  IntuiCommandShortcut,
}
